import React, { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../../api/client";
import { format } from "date-fns";
import {
	Area,
	AreaChart,
	Bar,
	BarChart,
	CartesianGrid,
	ComposedChart,
	Line,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import {
	AlertTriangle,
	ArrowLeft,
	Calendar,
	ChevronLeft,
	ChevronRight,
	Info,
	Loader2,
	RefreshCw,
	TrendingDown,
	TrendingUp,
	X,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from "../ui/table";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { useFormatting } from "../../hooks/useFormatting";
import { DEMO_TRANSACTIONS_LEGACY, FORECAST_90_DAY_ENDPOINT } from "../../data/demo-data";
import { BANK_ACCOUNTS } from "../../data/financial-systems";
import { useTerminology } from "@/hooks/useTerminology";
import { useUserPreferenceDefaults } from "@/contexts/UserPreferenceDefaultsContext";
import { InfoTooltip } from "@/components/tours/InfoTooltip";
import { getCurrencySymbol } from "@/utils/currency";
import { cn } from "../../lib/utils";
import { useVisibleScenarios } from "../../hooks/useVisibleScenarios";
import {
	BASE_ID,
	ClusterPickerPopover,
	SCENARIO_COLOURS,
	ScenarioOverlayPicker,
	scenarioLabel,
} from "./ScenarioOverlayPicker";
import type { ClusterCandidate } from "./ScenarioOverlayPicker";
import { useBaseForecast } from "../../hooks/useBaseForecast";
import { useForecast } from "../../hooks/useForecast";
import { useScenarioForecasts } from "../../hooks/useScenarioForecasts";
import { useApp } from "../../contexts/AppContext";
import { usePermissions } from "../../hooks/usePermissions";
import { applyScenarioToForecast } from "../../lib/scenarioEngine";
import type { ForecastDataPointResponseApi } from "../../api/models/forecast";
import type { ProjectedForecastPointResponseApi } from "../../api/models/scenario";
import type { TransactionCategory } from "../../types";
import { useForecastRunTransactions } from "../../hooks/useForecastRunTransactions";



const CHART_LINE_COLOR = 'var(--accent-sky-500)';
const CHART_THRESHOLD_COLOR = 'var(--mc-error-icon)';

type ViewType = 'line' | 'bar' | 'combined';

// Generate 12 months of forecast data starting from current month
const generate12MonthData = (
	scenario: "base" | "conservative" | "optimistic",
) => {
	const currentDate = new Date();
	const monthNames = [
		"Jan",
		"Feb",
		"Mar",
		"Apr",
		"May",
		"Jun",
		"Jul",
		"Aug",
		"Sep",
		"Oct",
		"Nov",
		"Dec",
	];

	const months = [];
	for (let i = 0; i < 12; i++) {
		const monthIndex = (currentDate.getMonth() + i) % 12;
		months.push(monthNames[monthIndex]);
	}

	const baseValues = {
		base: [
			197000,
			145000,
			FORECAST_90_DAY_ENDPOINT,
			95000,
			118000,
			135000,
			122000,
			108000,
			98000,
			125000,
			150000,
			172000,
		],
		conservative: [
			185000, 130000, 55000, 75000, 92000, 105000, 95000, 85000, 78000, 98000,
			115000, 135000,
		],
		optimistic: [
			210000, 165000, 95000, 130000, 155000, 180000, 165000, 145000, 135000,
			165000, 195000, 225000,
		],
	};

	const inflows = {
		base: [
			140000, 138000, 115000, 145000, 165000, 155000, 145000, 125000, 118000,
			115000, 145000, 165000,
		],
		conservative: [
			125000, 125000, 100000, 130000, 145000, 140000, 135000, 115000, 105000,
			100000, 130000, 150000,
		],
		optimistic: [
			155000, 155000, 135000, 165000, 185000, 175000, 165000, 145000, 135000,
			130000, 165000, 185000,
		],
	};

	const outflows = {
		base: [
			190000, 190000, 189700, 120000, 140000, 138000, 128000, 138000, 132000,
			128000, 118000, 118000,
		],
		conservative: [
			187000, 188000, 175000, 130000, 148000, 143000, 135000, 142000, 138000,
			135000, 127000, 128000,
		],
		optimistic: [
			192000, 183000, 185000, 105000, 130000, 130000, 120000, 130000, 125000,
			120000, 110000, 105000,
		],
	};

	return months.map((month, idx) => ({
		month,
		balance: baseValues[scenario][idx],
		inflow: inflows[scenario][idx],
		outflow: outflows[scenario][idx],
		net: inflows[scenario][idx] - outflows[scenario][idx],
	}));
};

const DAYS_IN_MONTH = 30;

function aggregateScenarioTimelineToMonthsFull(
	points: ProjectedForecastPointResponseApi[],
): { balance: number; inflow: number; outflow: number }[] {
	const monthNames = [
		"Jan",
		"Feb",
		"Mar",
		"Apr",
		"May",
		"Jun",
		"Jul",
		"Aug",
		"Sep",
		"Oct",
		"Nov",
		"Dec",
	];
	const buckets = new Map<
		string,
		{ balance: number; inflow: number; outflow: number }
	>();

	for (const dp of points) {
		const d = new Date(dp.date);
		const key = `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
		const existing = buckets.get(key);
		if (existing) {
			existing.inflow += dp.scenarioIncoming;
			existing.outflow += dp.scenarioOutgoing;
			existing.balance = dp.scenarioBalance;
		} else {
			buckets.set(key, {
				inflow: dp.scenarioIncoming,
				outflow: dp.scenarioOutgoing,
				balance: dp.scenarioBalance,
			});
		}
	}

	return Array.from(buckets.values());
}

function aggregateTimelineToMonths(timeline: ForecastDataPointResponseApi[]): {
	month: string;
	balance: number;
	inflow: number;
	outflow: number;
	net: number;
}[] {
	const monthNames = [
		"Jan",
		"Feb",
		"Mar",
		"Apr",
		"May",
		"Jun",
		"Jul",
		"Aug",
		"Sep",
		"Oct",
		"Nov",
		"Dec",
	];
	const buckets = new Map<
		string,
		{ balance: number; inflow: number; outflow: number }
	>();

	for (const dp of timeline) {
		const d = new Date(dp.date);
		const key = `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
		const existing = buckets.get(key);
		if (existing) {
			existing.inflow += dp.incoming;
			existing.outflow += dp.outgoing;
			existing.balance = dp.balance;
		} else {
			buckets.set(key, {
				inflow: dp.incoming,
				outflow: dp.outgoing,
				balance: dp.balance,
			});
		}
	}

	return Array.from(buckets.entries()).map(([month, data]) => ({
		month,
		balance: data.balance,
		inflow: data.inflow,
		outflow: data.outflow,
		net: data.inflow - data.outflow,
	}));
}

// ─── Shared types ─────────────────────────────────────────────────────────────
type CategoryRow = { category: TransactionCategory | 'Revenue' | 'Other'; label: string; amount: number; type: 'inflow' | 'outflow'; };

// ─── Shared colour constants (mirror RecentTransactionsCard exactly) ──────────
const DRILL_TEAL    = 'oklch(0.5 0.0896 194.8)';
const NEUTRAL_TXT   = '#6b7280';
const CONTENT_TXT   = '#111827';
const HDR_TXT       = 'oklch(0.2764 0.021 248.59)';

// ─── MonthlyRow ───────────────────────────────────────────────────────────────
function MonthlyRow({ row, idx, openingBalance, formatCurrency, onDrill }: {
	row: { month: string; inflow: number; outflow: number; net: number; balance: number };
	idx: number;
	openingBalance: number;
	formatCurrency: ReturnType<typeof import('../../hooks/useFormatting').useFormatting>['formatCurrency'];
	onDrill: () => void;
}) {
	return (
		<TableRow
			className={cn("cursor-pointer hover:bg-muted/50 border-0", idx % 2 === 0 && "bg-neutral-50")}
			onClick={onDrill}
		>
			<TableCell className="font-medium text-primary hover:underline">{row.month}</TableCell>
			<TableCell className="text-right tabular-nums">{formatCurrency(openingBalance)}</TableCell>
			<TableCell className="text-right tabular-nums text-success-600">{formatCurrency(row.inflow)}</TableCell>
			<TableCell className="text-right tabular-nums text-destructive">{formatCurrency(row.outflow)}</TableCell>
			<TableCell className="text-right tabular-nums">{formatCurrency(row.net)}</TableCell>
			<TableCell className="text-right tabular-nums">{formatCurrency(row.balance)}</TableCell>
			<TableCell className="text-right text-muted-foreground"><ChevronRight className="h-4 w-4 inline" /></TableCell>
		</TableRow>
	);
}

// ─── DrillCategoryRow ─────────────────────────────────────────────────────────
function DrillCategoryRow({ cat, index, incomingTerm, outgoingTerm, formatCurrency, onClick }: {
	cat: CategoryRow;
	index: number;
	incomingTerm: string;
	outgoingTerm: string;
	formatCurrency: ReturnType<typeof useFormatting>['formatCurrency'];
	onClick: () => void;
}) {
	const [rowHovered, setRowHovered] = useState(false);
	const [cellHovered, setCellHovered] = useState(false);
	const rowBg = rowHovered ? '#f1f5f9' : index % 2 === 0 ? '#ffffff' : '#f9fafb';

	return (
		<tr
			onClick={onClick}
			onMouseEnter={() => setRowHovered(true)}
			onMouseLeave={() => setRowHovered(false)}
			style={{ cursor: 'pointer' }}
		>
			<td
				onMouseEnter={() => setCellHovered(true)}
				onMouseLeave={() => setCellHovered(false)}
				style={{
					padding: '10px 12px',
					fontSize: 14,
					backgroundColor: rowBg,
					color: DRILL_TEAL,
					textDecoration: cellHovered || rowHovered ? 'underline' : 'none',
					transition: 'text-decoration 0.1s',
					whiteSpace: 'nowrap',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
				}}
			>
				{cat.label}
			</td>
			<td style={{ padding: '10px 12px', fontSize: 14, backgroundColor: rowBg, color: CONTENT_TXT }}>
				{cat.type === 'inflow' ? incomingTerm : outgoingTerm}
			</td>
			<td style={{ padding: '10px 12px', fontSize: 14, backgroundColor: rowBg, textAlign: 'right', color: cat.type === 'inflow' ? 'var(--mc-success-icon)' : 'var(--mc-error-icon)', fontVariantNumeric: 'tabular-nums' }}>
				{cat.type === 'outflow' ? '−' : ''}{formatCurrency(cat.amount, 'full')}
			</td>
			<td style={{ padding: '10px 12px', fontSize: 14, backgroundColor: rowBg, textAlign: 'right', color: NEUTRAL_TXT }}>
				<ChevronRight className="h-3.5 w-3.5 inline" />
			</td>
		</tr>
	);
}

// ─── TxnRow ───────────────────────────────────────────────────────────────────
function TxnRow({ txn, index, type, formatCurrency }: {
	txn: { id: string; description: string; amount: number; category: string; dataSourceName?: string };
	index: number;
	type: 'inflow' | 'outflow';
	formatCurrency: ReturnType<typeof useFormatting>['formatCurrency'];
}) {
	const [rowHovered, setRowHovered] = useState(false);
	const rowBg = rowHovered ? '#e5e7eb' : index % 2 === 0 ? '#ffffff' : '#f9fafb';
	const isManual = txn.dataSourceName?.startsWith('Manual Entry');

	return (
		<tr
			onMouseEnter={() => setRowHovered(true)}
			onMouseLeave={() => setRowHovered(false)}
		>
			{/* Description */}
			<td
				style={{
					padding: '10px 12px',
					fontSize: 14,
					backgroundColor: rowBg,
					color: CONTENT_TXT,
					whiteSpace: 'nowrap',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					maxWidth: 260,
				}}
			>
				{txn.description}
			</td>
			{/* Source */}
			<td style={{ padding: '10px 12px', fontSize: 14, backgroundColor: rowBg, color: CONTENT_TXT }}>
				{txn.dataSourceName && (
					<span style={{
						display: 'inline-flex',
						alignItems: 'center',
						padding: '2px 8px',
						borderRadius: 9999,
						fontSize: 12,
						fontWeight: 500,
						backgroundColor: isManual ? 'var(--mc-warning-bg)' : 'var(--mc-neutral-bg)',
						color: isManual ? 'var(--mc-warning-icon)' : 'var(--mc-neutral-icon)',
					}}>
						{txn.dataSourceName}
					</span>
				)}
			</td>
			{/* Amount */}
			<td style={{ padding: '10px 12px', fontSize: 14, backgroundColor: rowBg, textAlign: 'right', color: type === 'inflow' ? 'var(--mc-success-icon)' : 'var(--mc-error-icon)', fontVariantNumeric: 'tabular-nums' }}>
				{type === 'outflow' ? '−' : ''}{formatCurrency(txn.amount, 'full')}
			</td>
		</tr>
	);
}

export const Forecast12Month: React.FC = () => {
	const { formatCurrency } = useFormatting();
	const scenarios = useVisibleScenarios();
	const { baseForecast, forecastNotReady } = useBaseForecast();
	const { mode, handleNavigate } = useApp();
	const { hasPermission } = usePermissions();
	const { getForecastTerm, getCashMovementTerms } = useTerminology();
	const isLive = mode === "live";
	const {
		data: forecastResponse,
		isLoading: isForecastLoading,
		error: forecastError,
	} = useForecast("12m");
	const [viewType, setViewType] = useState<ViewType>("combined");
	const [selectedIds, setSelectedIds] = useState<string[]>([BASE_ID]);

	const nonBaseIds = selectedIds.filter((id) => id !== BASE_ID);
	const { data: scenarioForecastData } = useScenarioForecasts(
		nonBaseIds,
		"12m",
	);

	const { defaults: chartDefaults } = useUserPreferenceDefaults();
	const threshold = chartDefaults.minCashThreshold;
	const { in: incomingTerm, out: outgoingTerm } = getCashMovementTerms();

	// --- Drill-down state ---
	type DrillLevel = 'category' | 'transactions';
	type TxnRow = { id: string; description: string; amount: number; category: string; dataSourceName?: string; }

	const [drillMonth, setDrillMonth] = useState<{
		month: string;
		inflow: number;
		outflow: number;
		net: number;
		opening: number;
		closing: number;
		scenarioName?: string;
	} | null>(null);
	const [drillLevel, setDrillLevel] = useState<DrillLevel>("category");
	const [drillCategory, setDrillCategory] = useState<CategoryRow | null>(null);

	// Normalised shape used by drill-down helpers
	interface NormalizedTransaction {
		id?: string | number;
		name: string;
		description: string | null;
		grossValueIn: number;
		grossValueOut: number;
		category: string | null;
		transactionDate: string | null;
		dataSourceName?: string;
	}

	// Compute date range for the drilled-down month (used by the hook)
	const drillDateRange = (() => {
		if (!drillMonth) return { fromDate: undefined, toDate: undefined };
		const monthNames = [
			"Jan", "Feb", "Mar", "Apr", "May", "Jun",
			"Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
		];
		const parts = drillMonth.month.split(" ");
		const monthIdx = monthNames.indexOf(parts[0]);
		const year = parts[1]
			? Number.parseInt(parts[1], 10)
			: new Date().getFullYear();
		if (monthIdx < 0 || Number.isNaN(year)) return { fromDate: undefined, toDate: undefined };
		const startDate = new Date(year, monthIdx, 1);
		const endDate = new Date(year, monthIdx + 1, 0);
		return {
			fromDate: format(startDate, "yyyy-MM-dd"),
			toDate: format(endDate, "yyyy-MM-dd"),
		};
	})();

	// Fetch forecast run transactions for the drilled-down month (live mode)
	const forecastRunId = forecastResponse?.forecastRunId;
	const {
		data: rawRunTransactions,
		isLoading: isRunTxnLoading,
	} = useForecastRunTransactions(
		forecastRunId,
		drillDateRange.fromDate,
		drillDateRange.toDate,
		isLive && !!drillMonth,
	);

	// Map forecast run transactions to the normalised shape.
	// In live mode, default to an empty array (not null) so we never fall through to demo data.
	const liveMonthTransactions: NormalizedTransaction[] | null =
		isLive && drillMonth
			? (rawRunTransactions ?? []).map((txn) => ({
					id: txn.id,
					name: txn.counterpartyName ?? "Unknown",
					description: txn.description,
					grossValueIn: txn.direction === "inflow" ? txn.outstandingAmount : 0,
					grossValueOut: txn.direction === "outflow" ? txn.outstandingAmount : 0,
					category: txn.category,
					transactionDate: txn.forecastDate,
					dataSourceName: txn.dataSourceName ?? undefined,
				}))
			: null;

	const isDrillLoading = isLive && !!drillMonth && isRunTxnLoading;

	// Build category breakdown for a given month row
	const buildCategoryBreakdown = (
		monthName: string,
		inflow: number,
		outflow: number,
	): CategoryRow[] => {
		if (isLive && liveMonthTransactions) {
			// Live mode: group actual transactions by category (empty array → no rows)
			const inflowByCategory: Record<string, number> = {};
			const outflowByCategory: Record<string, number> = {};

			for (const txn of liveMonthTransactions) {
				const cat = txn.category ?? "Uncategorised";
				if (txn.grossValueIn > 0) {
					inflowByCategory[cat] =
						(inflowByCategory[cat] ?? 0) + txn.grossValueIn;
				}
				if (txn.grossValueOut > 0) {
					outflowByCategory[cat] =
						(outflowByCategory[cat] ?? 0) + txn.grossValueOut;
				}
			}

			const inflowRows: CategoryRow[] = Object.entries(inflowByCategory).map(
				([cat, total]) => ({
					category: cat as TransactionCategory,
					label: cat,
					amount: total,
					type: "inflow" as const,
				}),
			);

			const outflowRows: CategoryRow[] = Object.entries(outflowByCategory).map(
				([cat, total]) => ({
					category: cat as TransactionCategory,
					label: cat,
					amount: total,
					type: "outflow" as const,
				}),
			);

			return [...inflowRows, ...outflowRows];
		}

		// Demo mode: derive proportional splits from legacy transactions (representative sample)
		const txns = DEMO_TRANSACTIONS_LEGACY;
		const expenseTxns = txns.filter((t) => t.type === "expense");
		const totalExpense = expenseTxns.reduce(
			(s, t) => s + Math.abs(t.amount),
			0,
		);

		const expenseByCategory = expenseTxns.reduce<Record<string, number>>(
			(acc, t) => {
				const key = t.category as string;
				acc[key] = (acc[key] ?? 0) + Math.abs(t.amount);
				return acc;
			},
			{},
		);

		const outflowRows: CategoryRow[] = Object.entries(expenseByCategory).map(
			([cat, total]) => ({
				category: cat as TransactionCategory,
				label: cat,
				amount: Math.round((total / totalExpense) * outflow),
				type: "outflow" as const,
			}),
		);

		// Normalise rounding on outflows
		const outflowSum = outflowRows.reduce((s, r) => s + r.amount, 0);
		if (outflowRows.length > 0) outflowRows[0].amount += outflow - outflowSum;

		const inflowRows: CategoryRow[] = [
			{
				category: "Revenue" as TransactionCategory,
				label: "Customer Receipts",
				amount: Math.round(inflow * 0.9),
				type: "inflow",
			},
			{
				category: "Other Income" as TransactionCategory,
				label: "Other Income",
				amount: inflow - Math.round(inflow * 0.9),
				type: "inflow",
			},
		];

		return [...inflowRows, ...outflowRows];
	};

	// Pre-compute drill-down categories and derive header totals from them.
	// This guarantees the header cards always match the category rows beneath.
	const drillCategories = drillMonth
		? buildCategoryBreakdown(drillMonth.month, drillMonth.inflow, drillMonth.outflow)
		: [];
	const drillHeaderTotals = (() => {
		if (!isLive || drillCategories.length === 0) return null;
		const totalIn = drillCategories
			.filter((c) => c.type === "inflow")
			.reduce((s, c) => s + c.amount, 0);
		const totalOut = drillCategories
			.filter((c) => c.type === "outflow")
			.reduce((s, c) => s + c.amount, 0);
		return { inflow: totalIn, outflow: totalOut, net: totalIn - totalOut };
	})();

	// Build transaction rows for a given category in a month
	const buildTransactionRows = (category: CategoryRow): TxnRow[] => {
		if (isLive && liveMonthTransactions) {
			// Live mode: filter actual transactions by category and direction
			const filtered = liveMonthTransactions.filter((txn) => {
				const cat = txn.category ?? "Uncategorised";
				if (cat !== category.category) return false;
				return category.type === "inflow"
					? txn.grossValueIn > 0
					: txn.grossValueOut > 0;
			});

			if (filtered.length === 0) {
				return [
					{
						id: "live-empty",
						description: `${category.label} — no matching transactions`,
						amount: category.amount,
						category: category.label,
					},
				];
			}

			return filtered.map((txn) => ({
				id: String(txn.id),
				description: txn.description
					? `${txn.name} - ${txn.description}`
					: txn.name,
				amount:
					category.type === "inflow" ? txn.grossValueIn : txn.grossValueOut,
				category: txn.category ?? "Uncategorised",
				dataSourceName: txn.dataSourceName,
			}));
		}

		// Demo mode: scale from legacy transactions
		const base = DEMO_TRANSACTIONS_LEGACY.filter((t) =>
			category.type === "inflow"
				? t.type === "income"
				: t.category === category.category,
		);
		if (base.length === 0) {
			return [
				{
					id: "sys-1",
					description: `${category.label} — system generated`,
					amount: category.amount,
					category: category.label,
					dataSourceName: "Access Financials",
				},
			];
		}
		// Scale amounts proportionally to match the category total
		const rawTotal = base.reduce((s, t) => s + Math.abs(t.amount), 0);
		return base
			.map((t, i) => ({
				id: t.id,
				description: t.description,
				amount:
					i < base.length - 1
						? Math.round((Math.abs(t.amount) / rawTotal) * category.amount)
						: 0, // placeholder — fixed below
				category: t.category as string,
				dataSourceName: (t as any).dataSourceName ?? "Access Financials",
			}))
			.map((r, i, arr) => {
				if (i === arr.length - 1) {
					const soFar = arr.slice(0, -1).reduce((s, x) => s + x.amount, 0);
					return { ...r, amount: category.amount - soFar };
				}
				return r;
			});
	};

	const toggleScenario = (id: string) => {
		if (id === BASE_ID) {
			if (selectedIds.length === 1 && selectedIds[0] === BASE_ID) return;
			setSelectedIds((prev) =>
				prev.includes(BASE_ID)
					? prev.filter((s) => s !== BASE_ID)
					: [BASE_ID, ...prev.filter((s) => s !== BASE_ID)],
			);
		} else {
			setSelectedIds((prev) =>
				prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
			);
		}
	};

	const colourFor = (id: string, selectionIndex: number) =>
		SCENARIO_COLOURS[selectionIndex % SCENARIO_COLOURS.length];

	// Label for chart tooltips/legends — includes status
	const labelFor = (id: string) => {
		if (id === BASE_ID) return `Base ${getForecastTerm(true)}`;
		const sc = scenarios.find((s) => s.id === id);
		if (!sc) return id;
		return scenarioLabel(sc);
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const triggerMonthDrillDown = (row: any, scenarioId: string = BASE_ID) => {
		if (!row?.month) return;
		const currentCashBalance =
			isLive && forecastResponse
				? forecastResponse.metrics.startingCash
				: BANK_ACCOUNTS.reduce((sum, a) => sum + a.balance, 0);
		const rowIndex = baseData.findIndex((r) => r.month === row.month);

		const isScenario = scenarioId !== BASE_ID;
		const closingBalance = isScenario
			? ((row[scenarioId] as number) ??
				baseData[rowIndex]?.balance ??
				row.balance)
			: (baseData[rowIndex]?.balance ?? row.balance);
		const prevRow = rowIndex > 0 ? chartData[rowIndex - 1] : null;
		const openingBalance =
			rowIndex === 0
				? currentCashBalance
				: isScenario
					? ((prevRow?.[scenarioId] as number) ??
						baseData[rowIndex - 1].balance)
					: baseData[rowIndex - 1].balance;

		const scenarioName = isScenario
			? scenarios.find((s) => s.id === scenarioId)?.name
			: undefined;

		const inflow = isScenario
			? ((row[`_inflow_${scenarioId}`] as number) ?? row.inflow)
			: row.inflow;
		const outflow = isScenario
			? ((row[`_outflow_${scenarioId}`] as number) ?? row.outflow)
			: row.outflow;
		const net = isScenario
			? ((row[`_net_${scenarioId}`] as number) ?? row.net)
			: row.net;

		setDrillMonth({
			month: row.month,
			inflow,
			outflow,
			net,
			opening: openingBalance,
			closing: closingBalance,
			scenarioName,
		});
		setDrillLevel("category");
		setDrillCategory(null);
	};

	// ── Cluster picker state ─────────────────────────────────────────────────
	type ClusterStateType12 = {
		candidates: ClusterCandidate[];
		periodLabel: string;
		anchorX: number;
		anchorY: number;
		row: Record<string, unknown>;
	} | null;

	const [clusterState, setClusterState] = useState<ClusterStateType12>(null);
	// Chart container the cluster picker is anchored to, so it scrolls with the page.
	const chartContainerRef = useRef<HTMLDivElement>(null);
	const clusterStateRef = useRef<ClusterStateType12>(null);
	const setCluster = (val: ClusterStateType12) => {
		clusterStateRef.current = val;
		setClusterState(val);
	};

	// ── Pill scroll state ──────────────────────────────────────────────────────
	const pillsScrollRef = useRef<HTMLDivElement>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	const updateScrollState = useCallback(() => {
		const el = pillsScrollRef.current;
		if (!el) return;
		setCanScrollLeft(el.scrollLeft > 0);
		setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
	}, []);

	useEffect(() => {
		updateScrollState();
	});

	const scrollPills = (dir: 'left' | 'right') => {
		const el = pillsScrollRef.current;
		if (!el) return;
		el.scrollBy({ left: dir === 'right' ? 120 : -120, behavior: 'smooth' });
	};

	// Recharts v3: track the hovered index via onMouseMove and read on click.
	const activeChartIdxRef = useRef<number | null>(null);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleChartMouseMove = (state: any) => {
		const raw = state?.activeTooltipIndex ?? state?.activeIndex;
		activeChartIdxRef.current = raw != null ? Number(raw) : null;
	};
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleChartClick = (_state: any, event?: React.MouseEvent) => {
		const idx = activeChartIdxRef.current;
		if (idx == null || Number.isNaN(idx) || !chartData[idx]) return;
		const row = chartData[idx];

		// Build candidate list from every selected series that has a value at this point
		const candidates: ClusterCandidate[] = selectedIds
			.filter((id) => typeof row[id] === "number")
			.map((id) => ({
				id,
				label: labelFor(id),
				value: row[id] as number,
				colour: colourFor(id, selectedIds.indexOf(id)),
			}));

		if (candidates.length === 0) return;

		// Single series → fire directly, no picker needed
		if (candidates.length === 1) {
			triggerMonthDrillDown(row, candidates[0].id);
			return;
		}

		// Multiple series → show cluster picker at click coordinates
		if (event) {
			setCluster({
				candidates,
				periodLabel: row.month as string,
				anchorX: event.clientX,
				anchorY: event.clientY,
				row,
			});
		}
	};



	const handleBarClick = (data: any) => {
		if (data?.activePayload?.[0])
			triggerMonthDrillDown(data.activePayload[0].payload);
		else if (data?.month) triggerMonthDrillDown(data);
	};

	// Build merged chart data: one row per month with a key per selected scenario
	const chartData = (() => {
		// Generate base data rows (we always need the month labels and inflow/outflow from base)
		const baseRows =
			isLive && forecastResponse
				? aggregateTimelineToMonths(forecastResponse.timeline)
				: generate12MonthData("base");

		// Pre-generate data for each selected non-base scenario
		const scenarioRows: Record<string, number[]> = {};
		const scenarioFlows: Record<string, { inflow: number; outflow: number }[]> =
			{};
		for (const id of selectedIds) {
			if (id !== BASE_ID) {
				if (isLive) {
					const forecast = scenarioForecastData[id];
					if (forecast) {
						const fullData = aggregateScenarioTimelineToMonthsFull(
							forecast.projectedForecast,
						);
						scenarioRows[id] = fullData.map((d) => d.balance);
						scenarioFlows[id] = fullData.map((d) => ({
							inflow: d.inflow,
							outflow: d.outflow,
						}));
					}
				} else {
					const scenario = scenarios.find((s) => s.id === id);
					if (scenario && baseForecast.length > 0) {
						if (scenario.changes.length === 0) {
							scenarioFlows[id] = baseRows.map((d) => ({
								inflow: d.inflow,
								outflow: d.outflow,
							}));
							scenarioRows[id] = Array.from({ length: 12 }, (_, i) => {
								const dayIndex = Math.min(
									(i + 1) * DAYS_IN_MONTH,
									baseForecast.length - 1,
								);
								return baseForecast[dayIndex]?.balance ?? 0;
							});
						} else {
							const result = applyScenarioToForecast(
								baseForecast,
								scenario.changes,
							);
							const projected = result.projectedForecast;
							const netMonthlyDelta = result.metrics.netImpact / 12;
							const totalBaseInflow = baseRows.reduce(
								(s, r) => s + r.inflow,
								0,
							);
							const totalBaseOutflow = baseRows.reduce(
								(s, r) => s + r.outflow,
								0,
							);
							const inflowScale =
								totalBaseInflow > 0
									? (totalBaseInflow + result.metrics.revenueChangeAmount) /
										totalBaseInflow
									: 1;
							const outflowScale =
								totalBaseOutflow > 0
									? (totalBaseOutflow + result.metrics.costChangeAmount) /
										totalBaseOutflow
									: 1;
							scenarioFlows[id] = baseRows.map((d) => ({
								inflow: Math.round(d.inflow * inflowScale),
								outflow: Math.round(d.outflow * outflowScale),
							}));
							scenarioRows[id] = Array.from({ length: 12 }, (_, i) => {
								if (i < 3) {
									const dayIndex = Math.min(
										(i + 1) * DAYS_IN_MONTH,
										projected.length - 1,
									);
									return projected[dayIndex]?.scenarioBalance ?? 0;
								}
								const baseMonthDayIndex = Math.min(
									(i + 1) * DAYS_IN_MONTH,
									baseForecast.length - 1,
								);
								const baseBalance =
									baseForecast[baseMonthDayIndex]?.balance ?? 0;
								return Math.round(baseBalance + netMonthlyDelta * (i + 1));
							});
						}
					}
				}
			}
		}

		return baseRows.map((row, idx) => {
			const merged: Record<string, unknown> = {
				month: row.month,
				inflow: row.inflow,
				outflow: row.outflow,
				net: row.net,
			};
			if (selectedIds.includes(BASE_ID)) {
				merged[BASE_ID] = row.balance;
			}
			for (const [id, balances] of Object.entries(scenarioRows)) {
				merged[id] = balances[idx];
				const flows = scenarioFlows[id]?.[idx];
				if (flows) {
					merged[`_inflow_${id}`] = flows.inflow;
					merged[`_outflow_${id}`] = flows.outflow;
					merged[`_net_${id}`] = flows.inflow - flows.outflow;
				}
			}
			return merged;
		});
	})();

	// Metrics are calculated from the base data for consistency
	const baseData =
		isLive && forecastResponse
			? aggregateTimelineToMonths(forecastResponse.timeline)
			: generate12MonthData("base");
	const lowestBalance = Math.min(...baseData.map((d) => d.balance));
	const highestBalance = Math.max(...baseData.map((d) => d.balance));
	const averageBalance =
		baseData.reduce((sum, d) => sum + d.balance, 0) / baseData.length;
	const totalInflow = baseData.reduce((sum, d) => sum + d.inflow, 0);
	const totalOutflow = baseData.reduce((sum, d) => sum + d.outflow, 0);
	const netCashFlow = totalInflow - totalOutflow;
	const lowestMonth =
		baseData.find((d) => d.balance === lowestBalance)?.month ?? "N/A";
	const highestMonth =
		baseData.find((d) => d.balance === highestBalance)?.month ?? "N/A";

	// Recovery detection: find the last month at the minimum balance (real turning point)
	const lowestIndex = baseData.reduce(
		(lastIdx, d, i) => (d.balance <= lowestBalance ? i : lastIdx),
		-1,
	);
	const troughMonth = baseData[lowestIndex]?.month ?? "N/A";
	const lastMonth = baseData[baseData.length - 1]?.month ?? "N/A";
	const recoveryMargin = averageBalance * 0.05;
	const hasRecovery =
		lowestIndex >= 0 &&
		lowestIndex < baseData.length - 1 &&
		baseData
			.slice(lowestIndex + 1)
			.some((d) => d.balance > lowestBalance + recoveryMargin);
	const tailDeclining =
		!hasRecovery &&
		baseData.length > 1 &&
		baseData[baseData.length - 1].balance < baseData[Math.max(0, lowestIndex)].balance;

	// Find the exact peak month before the trough for the decline callout
	const declineStartIndex = baseData
		.slice(0, lowestIndex + 1)
		.reduce((peakIdx, d, i, arr) => (d.balance > arr[peakIdx].balance ? i : peakIdx), 0);
	const declineStartMonth = baseData[declineStartIndex]?.month ?? "N/A";

	if (!hasPermission('view_12month_forecast_screen')) return null;

	const CustomTooltip = ({ active, payload, label }: any) => {
		if (active && payload && payload.length) {
			return (
				<div className="bg-popover text-popover-foreground border shadow-md rounded-md px-3 py-2 text-sm">
					<p className="font-semibold mb-1.5">{label}</p>
					{payload.filter((e: any) => e.type !== 'none').map((entry: any, index: number) => (
						<div key={index} className="flex items-center gap-2">
							<div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
							<span className="text-muted-foreground capitalize">{entry.name}:</span>
							<span className="font-semibold ml-auto pl-3">{formatCurrency(entry.value)}</span>
						</div>
					))}
				</div>
			);
		}
		return null;
	};

	if (isLive && isForecastLoading) {
		return (
			<div className="space-y-6 p-6">
				<Card>
					<CardContent className="pt-6">
						<div className="flex items-center justify-center py-12">
							<div className="text-muted-foreground">
								Loading 12-month {getForecastTerm(false)} data...
							</div>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	if (isLive && (forecastNotReady || (forecastError instanceof ApiError && forecastError.status === 404))) {
		return (
			<div className="p-6">
				<Card className="border-dashed">
					<CardContent className="flex flex-col items-center justify-center py-16 text-center">
						<TrendingUp className="h-12 w-12 text-muted-foreground/40 mb-4" />
						<h3 className="text-lg font-semibold mb-2">
							No {getForecastTerm(false)} data available
						</h3>
						<p className="text-muted-foreground text-sm max-w-md mb-6">
							A {getForecastTerm(false)} refresh has not been run yet. Run a{" "}
							{getForecastTerm(false)} refresh to populate this screen with cash
							position data and projections.
						</p>
						{hasPermission("trigger_forecast_refresh") && (
							<Button onClick={() => handleNavigate("forecast-refresh")}>
								<RefreshCw className="h-4 w-4 mr-2" />
								Go to Refresh {getForecastTerm(true)}
							</Button>
						)}
					</CardContent>
				</Card>
			</div>
		);
	}

	if (isLive && forecastError) {
		return (
			<div className="space-y-6 p-6">
				<Card>
					<CardContent className="pt-6">
						<div className="text-center text-destructive py-12">
							{forecastError instanceof Error
								? forecastError.message
								: "Failed to load 12-month forecast"}
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className="space-y-6 p-6">

			{/* Key Metrics */}
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
				{/* Lowest Balance */}
				<Card className="h-full" style={{ borderLeft: `3px solid ${lowestBalance < threshold ? 'var(--mc-error-border)' : 'var(--mc-warning-border)'}` }}>
					<CardContent className="p-4 flex flex-col justify-between h-full gap-4">
						<div className="flex items-start justify-between gap-2">
							<span className="text-base font-medium text-neutral-800">Lowest Balance</span>
							<div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
								style={{ backgroundColor: lowestBalance < threshold ? 'var(--mc-error-bg)' : 'var(--mc-warning-bg)' }}>
								<AlertTriangle className="h-4 w-4" style={{ color: lowestBalance < threshold ? 'var(--mc-error-icon)' : 'var(--mc-warning-icon)' }} />
							</div>
						</div>
						<div>
							<div className="text-2xl font-bold text-neutral-900 tracking-tight">{formatCurrency(lowestBalance)}</div>
							<div className="text-sm text-neutral-800 mt-1">
								{lowestBalance < threshold ? `⚠ Below threshold in ${lowestMonth}` : lowestMonth}
							</div>
						</div>
					</CardContent>
				</Card>

				{/* Highest Balance */}
				<Card className="h-full" style={{ borderLeft: '3px solid var(--mc-info-border)' }}>
					<CardContent className="p-4 flex flex-col justify-between h-full gap-4">
						<div className="flex items-start justify-between gap-2">
							<span className="text-base font-medium text-neutral-800">Highest Balance</span>
							<div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
								style={{ backgroundColor: 'var(--mc-info-bg)' }}>
								<TrendingUp className="h-4 w-4" style={{ color: 'var(--mc-info-icon)' }} />
							</div>
						</div>
						<div>
							<div className="text-2xl font-bold text-neutral-900 tracking-tight">{formatCurrency(highestBalance)}</div>
							<div className="text-sm text-neutral-800 mt-1">{highestMonth}</div>
						</div>
					</CardContent>
				</Card>

				{/* Average Balance */}
				<Card className="h-full" style={{ borderLeft: '3px solid var(--mc-info-border)' }}>
					<CardContent className="p-4 flex flex-col justify-between h-full gap-4">
						<div className="flex items-start justify-between gap-2">
							<span className="text-base font-medium text-neutral-800">Average Balance</span>
							<div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
								style={{ backgroundColor: 'var(--mc-info-bg)' }}>
								<Calendar className="h-4 w-4" style={{ color: 'var(--mc-info-icon)' }} />
							</div>
						</div>
						<div>
							<div className="text-2xl font-bold text-neutral-900 tracking-tight">{formatCurrency(averageBalance)}</div>
							<div className="text-sm text-neutral-800 mt-1">12-month average</div>
						</div>
					</CardContent>
				</Card>

				{/* Net Cash Flow */}
				<Card className="h-full" style={{ borderLeft: '3px solid var(--mc-info-border)' }}>
					<CardContent className="p-4 flex flex-col justify-between h-full gap-4">
						<div className="flex items-start justify-between gap-2">
							<span className="text-base font-medium text-neutral-800">Net Cash Flow</span>
							<div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
								style={{ backgroundColor: 'var(--mc-info-bg)' }}>
								{netCashFlow >= 0
									? <TrendingUp className="h-4 w-4" style={{ color: 'var(--mc-info-icon)' }} />
									: <TrendingDown className="h-4 w-4" style={{ color: 'var(--mc-info-icon)' }} />
								}
							</div>
						</div>
						<div>
							<div className="text-2xl font-bold text-neutral-900 tracking-tight">{formatCurrency(netCashFlow)}</div>
							<div className="text-sm text-neutral-800 mt-1">
								{netCashFlow >= 0 ? 'Positive' : 'Negative'} over 12 months
							</div>
						</div>
					</CardContent>
				</Card>
			</div>

			{/* Main Chart */}
			<Card>
				<div className="flex items-center justify-between flex-wrap gap-4 px-4 py-3 border-b">
					<div className="flex items-center gap-1">
						<CardTitle className="text-lg font-semibold text-neutral-900">
							Cash Balance {getForecastTerm(true)}
						</CardTitle>
						<InfoTooltip
							title={`Cash Balance ${getForecastTerm(true)}`}
							content="Your projected monthly cash position over the next 12 months, based on expected income and outgoings from all connected data sources."
						/>
					</div>
					<div className="flex items-center gap-4">
						<Tabs value={viewType} onValueChange={(v) => setViewType(v as ViewType)}>
							<TabsList>
								<TabsTrigger value="combined">Combined</TabsTrigger>
								<TabsTrigger value="line">Line</TabsTrigger>
								<TabsTrigger value="bar">Bar</TabsTrigger>
							</TabsList>
						</Tabs>
						<ScenarioOverlayPicker
							scenarios={scenarios}
							selectedIds={selectedIds}
							onToggle={toggleScenario}
						/>
						{selectedIds.filter(id => id !== BASE_ID).length > 0 && (
							<button
								type="button"
								onClick={() => setSelectedIds([BASE_ID])}
								className="text-sm text-muted-foreground hover:text-destructive transition-colors whitespace-nowrap"
							>
								Deselect all
							</button>
						)}
					</div>
				</div>

				{/* ── Active overlay pills — single scrollable row with arrow controls ── */}
				{selectedIds.filter(id => id !== BASE_ID).length > 0 && (
					<div className="relative border-b flex items-center">
						{canScrollLeft && (
							<button
								type="button"
								onClick={() => scrollPills('left')}
								className="flex-shrink-0 flex items-center justify-center w-7 h-full pl-2 text-muted-foreground hover:text-foreground transition-colors z-10"
								aria-label="Scroll left"
							>
								<ChevronLeft className="h-4 w-4" />
							</button>
						)}
						<div
							ref={pillsScrollRef}
							onScroll={updateScrollState}
							className="flex items-center gap-2 px-3 py-2 overflow-x-auto scrollbar-none flex-1"
							style={{ scrollBehavior: 'smooth' }}
						>
							{selectedIds.filter(id => id !== BASE_ID).map((id, idx) => {
								const sc = scenarios.find(s => s.id === id);
								return (
									<Badge
										key={id}
										variant="outline"
										className="flex-shrink-0 flex items-center gap-1.5 pr-1"
									>
										<span
											className="w-2 h-2 rounded-full flex-shrink-0"
											style={{ backgroundColor: SCENARIO_COLOURS[idx % SCENARIO_COLOURS.length] }}
										/>
										<span>{sc?.name ?? id}</span>
										<button
											type="button"
											onClick={() => toggleScenario(id)}
											className="ml-0.5 rounded-sm opacity-60 hover:opacity-100"
										>
											×
										</button>
									</Badge>
								);
							})}
						</div>
						{canScrollRight && (
							<button
								type="button"
								onClick={() => scrollPills('right')}
								className="flex-shrink-0 flex items-center justify-center w-7 h-full pr-2 text-muted-foreground hover:text-foreground transition-colors z-10"
								aria-label="Scroll right"
							>
								<ChevronRight className="h-4 w-4" />
							</button>
						)}
					</div>
				)}

				<CardContent>
					{/* Combined legend — above chart, matches 90-day style */}
					<div className="mb-3 flex flex-wrap items-center gap-6 text-sm">
						<div className="flex items-center gap-2">
							<div className="w-8 h-0.5 rounded-full" style={{ backgroundColor: CHART_LINE_COLOR }} />
							<span>Base {getForecastTerm(true)}</span>
						</div>
						{viewType === 'combined' && (
							<>
								<div className="flex items-center gap-2">
									<div className="w-4 h-3 rounded-sm opacity-70" style={{ backgroundColor: 'var(--mc-success-icon)' }} />
									<span>{incomingTerm}</span>
								</div>
								<div className="flex items-center gap-2">
									<div className="w-4 h-3 rounded-sm opacity-70" style={{ backgroundColor: 'var(--mc-error-icon)' }} />
									<span>{outgoingTerm}</span>
								</div>
							</>
						)}
						<div className="flex items-center gap-2">
							<div className="w-8 h-0 border-t-2 border-dashed" style={{ borderColor: CHART_THRESHOLD_COLOR }} />
							<span>Threshold ({getCurrencySymbol()}50K)</span>
						</div>

					</div>

					<div ref={chartContainerRef} className="relative h-[400px] w-full" style={{ cursor: 'pointer' }}>
						{clusterState && (
							<ClusterPickerPopover
								candidates={clusterState.candidates}
								periodLabel={clusterState.periodLabel}
								anchorX={clusterState.anchorX}
								anchorY={clusterState.anchorY}
								containerRef={chartContainerRef}
								onSelect={(id) => {
									triggerMonthDrillDown(clusterState.row, id);
									setCluster(null);
								}}
								onDismiss={() => setCluster(null)}
							/>
						)}
						<ResponsiveContainer width="100%" height="100%">
							{viewType === 'combined' ? (
								<ComposedChart data={chartData} onClick={handleChartClick} onMouseMove={handleChartMouseMove} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
									<CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
									<XAxis dataKey="month" tick={{ fontSize: 12 }} />
									<YAxis
										tick={{ fontSize: 12 }}
										tickFormatter={(value) =>
											`${getCurrencySymbol()}${(value / 1000).toFixed(0)}K`
										}
									/>
									<Tooltip content={clusterState ? () => null : <CustomTooltip />} />

									{/* Threshold line */}
									<ReferenceLine
										y={threshold}
										stroke={CHART_THRESHOLD_COLOR}
										strokeDasharray="5 5"
										strokeWidth={2}
										label={{ value: 'Threshold', position: 'insideTopLeft', fill: CHART_THRESHOLD_COLOR, fontSize: 12 }}
									/>

									{/* Inflow / Outflow bars always from base data */}
									<Bar dataKey="inflow" fill="var(--mc-success-icon)" name={incomingTerm} opacity={0.5} onClick={handleBarClick} cursor="pointer" />
									<Bar dataKey="outflow" fill="var(--mc-error-icon)" name={outgoingTerm} opacity={0.5} onClick={handleBarClick} cursor="pointer" />

									{/* Balance lines per selected scenario */}
									{selectedIds.map((id, idx) => (
										<Line
											key={`line-${id}`}
											type="monotone"
											dataKey={id}
											name={labelFor(id)}
											stroke={id === BASE_ID ? CHART_LINE_COLOR : colourFor(id, idx)}
											strokeWidth={id === BASE_ID ? 3 : 2}
											dot={false}
											activeDot={{ r: 5, fill: id === BASE_ID ? CHART_LINE_COLOR : colourFor(id, idx), cursor: 'pointer' }}
										/>
									))}
								</ComposedChart>
							) : viewType === 'line' ? (
								<ComposedChart data={chartData} onClick={handleChartClick} onMouseMove={handleChartMouseMove} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
									<CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
									<XAxis dataKey="month" tick={{ fontSize: 12 }} />
									<YAxis
										tick={{ fontSize: 12 }}
										tickFormatter={(value) =>
											`${getCurrencySymbol()}${(value / 1000).toFixed(0)}K`
										}
									/>
									<Tooltip content={clusterState ? () => null : <CustomTooltip />} />

									{/* Threshold line */}
									<ReferenceLine
										y={threshold}
										stroke={CHART_THRESHOLD_COLOR}
										strokeDasharray="5 5"
										strokeWidth={2}
										label={{ value: 'Threshold', position: 'insideTopLeft', fill: CHART_THRESHOLD_COLOR, fontSize: 12 }}
									/>

									{/* Balance lines per selected scenario */}
									{selectedIds.map((id, idx) => (
										<Line
											key={`line-${id}`}
											type="monotone"
											dataKey={id}
											name={labelFor(id)}
											stroke={id === BASE_ID ? CHART_LINE_COLOR : colourFor(id, idx)}
											strokeWidth={id === BASE_ID ? 3 : 2}
											dot={false}
											activeDot={{ r: 5, fill: id === BASE_ID ? CHART_LINE_COLOR : colourFor(id, idx), cursor: 'pointer' }}
										/>
									))}
								</ComposedChart>
							) : (
								<BarChart data={chartData} onClick={handleChartClick} onMouseMove={handleChartMouseMove} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
									<CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
									<XAxis dataKey="month" tick={{ fontSize: 12 }} />
									<YAxis
										tick={{ fontSize: 12 }}
										tickFormatter={(value) =>
											`${getCurrencySymbol()}${(value / 1000).toFixed(0)}K`
										}
									/>
									<Tooltip content={clusterState ? () => null : <CustomTooltip />} />

									{/* Threshold line */}
									<ReferenceLine
										y={threshold}
										stroke={CHART_THRESHOLD_COLOR}
										strokeDasharray="5 5"
										strokeWidth={2}
										label={{ value: 'Threshold', position: 'insideTopLeft', fill: CHART_THRESHOLD_COLOR, fontSize: 12 }}
									/>

									<Bar dataKey="inflow" fill="var(--mc-success-icon)" name={incomingTerm} onClick={handleBarClick} cursor="pointer" />
									<Bar dataKey="outflow" fill="var(--mc-error-icon)" name={outgoingTerm} onClick={handleBarClick} cursor="pointer" />
									{selectedIds.map((id, idx) => (
										<Bar
											key={id}
											dataKey={id}
											name={labelFor(id)}
											fill={id === BASE_ID ? CHART_LINE_COLOR : colourFor(id, idx)}
											opacity={0.8}
										/>
									))}
								</BarChart>
							)}
						</ResponsiveContainer>
					</div>


				</CardContent>
			</Card>

			{/* Monthly Breakdown Table */}
			<Card>
				<CardHeader>
					<CardTitle>Monthly Breakdown</CardTitle>
				</CardHeader>
				<CardContent className="p-0">
					<Table>
						<TableHeader className="sticky top-0 z-40 bg-white [&_tr]:border-0 after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-border after:z-50">
							<TableRow className="hover:bg-transparent border-0">
								<TableHead className="font-semibold text-foreground bg-white">Month</TableHead>
								<TableHead className="text-right font-semibold text-foreground bg-white">Opening Balance</TableHead>
								<TableHead className="text-right font-semibold text-foreground bg-white">{incomingTerm}</TableHead>
								<TableHead className="text-right font-semibold text-foreground bg-white">{outgoingTerm}</TableHead>
								<TableHead className="text-right font-semibold text-foreground bg-white">Net</TableHead>
								<TableHead className="text-right font-semibold text-foreground bg-white">Closing Balance</TableHead>
								<TableHead className="w-8 bg-white" />
							</TableRow>
						</TableHeader>
						<TableBody className="[&_tr]:border-b-0">
							{baseData.map((row, idx) => {
								const currentCashBalance =
									isLive && forecastResponse
										? forecastResponse.metrics.startingCash
										: BANK_ACCOUNTS.reduce((sum, a) => sum + a.balance, 0);
								const openingBalance =
									idx === 0 ? currentCashBalance : baseData[idx - 1].balance;
								return (
									<MonthlyRow
										key={row.month}
										row={row}
										idx={idx}
										openingBalance={openingBalance}
										formatCurrency={formatCurrency}
										onDrill={() => {
											setDrillMonth({ month: row.month, inflow: row.inflow, outflow: row.outflow, net: row.net, opening: openingBalance, closing: row.balance });
											setDrillLevel('category');
											setDrillCategory(null);
										}}
									/>
								);
							})}
						</TableBody>
						<TableFooter>
							<TableRow>
								<TableCell className="font-semibold">Total</TableCell>
								<TableCell className="text-right font-semibold">—</TableCell>
								<TableCell className="text-right text-success-600 tabular-nums font-semibold">{formatCurrency(totalInflow)}</TableCell>
								<TableCell className="text-right text-destructive tabular-nums font-semibold">{formatCurrency(totalOutflow)}</TableCell>
								<TableCell className="text-right tabular-nums font-semibold">{formatCurrency(netCashFlow)}</TableCell>
								<TableCell className="text-right font-semibold">—</TableCell>
								<TableCell />
							</TableRow>
						</TableFooter>
					</Table>
				</CardContent>
			</Card>

			{/* Insights */}
			<Card>
				<div className="flex items-center justify-between px-4 py-3 border-b">
					<div className="flex items-center gap-1">
						<span className="text-lg font-semibold text-neutral-900">Key Insights</span>
						<InfoTooltip
							title="Key Insights"
							content="Automated observations about your 12-month cash position, including any projected threshold breaches and the impact of active scenario overlays."
						/>
					</div>
				</div>
				<CardContent className="pt-4">
					<div className="space-y-3">
						{lowestBalance < threshold && (
							<div className="flex items-start gap-3 p-3 rounded-lg border" style={{ backgroundColor: 'var(--mc-error-bg)', borderColor: 'var(--mc-error-border)' }}>
								<AlertTriangle className="h-5 w-5 mt-0.5" style={{ color: 'var(--mc-error-icon)' }} />
								<div>
									<div className="font-semibold" style={{ color: 'var(--mc-error-icon)' }}>Cash Flow Warning</div>
									<div className="text-sm" style={{ color: 'var(--mc-error-icon)' }}>
										Your balance is projected to fall below the {formatCurrency(threshold, 'full')} threshold in {lowestMonth}.
										Consider securing additional financing or delaying non-essential payments.
									</div>
								</div>
							</div>
						)}

						{hasRecovery ? (
							<div className="flex items-start gap-3 p-3 rounded-lg border" style={{ backgroundColor: 'var(--mc-info-bg)', borderColor: 'var(--mc-info-border)' }}>
								<TrendingUp className="h-5 w-5 mt-0.5" style={{ color: 'var(--mc-info-icon)' }} />
								<div>
									<div className="font-semibold" style={{ color: 'var(--mc-info-icon)' }}>Recovery Expected After {troughMonth}</div>
									<div className="text-sm" style={{ color: 'var(--mc-info-icon)' }}>
										Cash position improves after {troughMonth} as delayed invoices are collected and new projects commence.
										Taking action now on the AI recommendations can accelerate this recovery.
									</div>
								</div>
							</div>
						) : (
							<div className="flex items-start gap-3 p-3 rounded-lg border" style={{ backgroundColor: 'var(--mc-warning-bg)', borderColor: 'var(--mc-warning-border)' }}>
								<TrendingDown className="h-5 w-5 mt-0.5" style={{ color: 'var(--mc-warning-icon)' }} />
								<div>
									<div className="font-semibold" style={{ color: 'var(--mc-warning-icon)' }}>
										{tailDeclining
											? `Cash Position Declining Since ${declineStartMonth}`
											: `Cash Position Flat Since ${declineStartMonth}`}
									</div>
									<div className="text-sm" style={{ color: 'var(--mc-warning-icon)' }}>
										No significant recovery is projected through the end of the forecast period.
										Review the AI recommendations for options to improve your cash position.
									</div>
								</div>
							</div>
						)}

						{/* Per-scenario insight callouts */}
						{selectedIds.filter(id => id !== BASE_ID).map((id) => {
							const sc = scenarios.find(s => s.id === id);
							if (!sc) return null;
							const colour = colourFor(id, selectedIds.indexOf(id));
							return (
								<div
									key={id}
									className="flex items-start gap-3 p-3 rounded-lg border"
									style={{ borderColor: `${colour}40`, backgroundColor: `${colour}0d` }}
								>
									<Info className="h-5 w-5 mt-0.5" style={{ color: colour }} />
									<div>
										<div className="font-semibold" style={{ color: colour }}>
											Scenario: {sc.name}
										</div>
									</div>
								</div>
							);
							})}
					</div>
				</CardContent>
			</Card>

			{/* ── Monthly Drill-Down Modal ── */}
			<Dialog
				open={!!drillMonth}
				onOpenChange={(open) => {
					if (!open) {
						setDrillMonth(null);
						setDrillLevel("category");
						setDrillCategory(null);
					}
				}}
			>
				<DialogContent className="max-w-2xl">
					<DialogHeader>
						<DialogTitle className="text-lg">
							{drillMonth?.month} — Cash Flow Breakdown{drillMonth?.scenarioName ? ` — ${drillMonth.scenarioName}` : ""}
						</DialogTitle>
					</DialogHeader>
					<div style={{ borderBottom: '1px solid #e5e7eb' }} />

					{drillMonth && (
						<div className="overflow-y-auto" style={{ maxHeight: '70vh' }}>
							{/* Summary bar */}
							<div className="grid grid-cols-4 gap-3 text-sm mb-4">
								<div className="rounded-lg border bg-card p-3" style={{ borderLeft: '3px solid var(--mc-info-border)' }}>
									<div className="text-xs text-foreground mb-1">Opening</div>
									<div className="font-semibold text-foreground">{formatCurrency(drillMonth.opening, 'full')}</div>
								</div>
								<div className="rounded-lg border bg-card p-3" style={{ borderLeft: '3px solid var(--mc-success-border)' }}>
									<div className="text-xs text-foreground mb-1">{incomingTerm}</div>
									<div className="font-semibold" style={{ color: 'var(--mc-success-icon)' }}>{isDrillLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : formatCurrency(drillHeaderTotals?.inflow ?? drillMonth.inflow, 'full')}</div>
								</div>
								<div className="rounded-lg border bg-card p-3" style={{ borderLeft: '3px solid var(--mc-error-border)' }}>
									<div className="text-xs text-foreground mb-1">{outgoingTerm}</div>
									<div className="font-semibold" style={{ color: 'var(--mc-error-icon)' }}>{isDrillLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : formatCurrency(drillHeaderTotals?.outflow ?? drillMonth.outflow, 'full')}</div>
								</div>
								<div className="rounded-lg border bg-card p-3" style={{ borderLeft: '3px solid var(--mc-info-border)' }}>
									<div className="text-xs text-foreground mb-1">Closing</div>
									<div className="font-semibold text-foreground">{formatCurrency(drillMonth.closing, 'full')}</div>
								</div>
							</div>

							{/* Level 1 — Category breakdown */}
							{drillLevel === 'category' && (
								<table className="w-full" style={{ borderCollapse: 'collapse' }}>
									<thead>
										<tr style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' }}>
											<th style={{ padding: '10px 12px', fontSize: 14, fontWeight: 600, color: HDR_TXT, textAlign: 'left' }}>Category</th>
											<th style={{ padding: '10px 12px', fontSize: 14, fontWeight: 600, color: HDR_TXT, textAlign: 'left' }}>Type</th>
											<th style={{ padding: '10px 12px', fontSize: 14, fontWeight: 600, color: HDR_TXT, textAlign: 'right' }}>Amount</th>
											<th style={{ width: 24 }} />
										</tr>
									</thead>
									<tbody>
										{drillCategories.map((cat, i) => (
											<DrillCategoryRow
												key={`${cat.label}-${i}`}
												cat={cat}
												index={i}
												incomingTerm={incomingTerm}
												outgoingTerm={outgoingTerm}
												formatCurrency={formatCurrency}
												onClick={() => {
													setDrillCategory(cat);
													setDrillLevel('transactions');
												}}
											/>
										))}
									</tbody>
									<tfoot>
										<tr style={{ borderTop: '2px solid #e5e7eb' }}>
											<td style={{ padding: '10px 12px', fontSize: 14, fontWeight: 600, color: CONTENT_TXT }}>Net</td>
											<td />
											<td style={{ padding: '10px 12px', fontSize: 14, fontWeight: 600, textAlign: 'right', color: (drillHeaderTotals?.net ?? drillMonth.net) >= 0 ? 'var(--mc-success-icon)' : 'var(--mc-error-icon)', fontVariantNumeric: 'tabular-nums' }}>
												{isDrillLoading ? <Loader2 className="h-4 w-4 animate-spin inline-block" /> : formatCurrency(drillHeaderTotals?.net ?? drillMonth.net, 'full')}
											</td>
											<td />
										</tr>
									</tfoot>
								</table>
							)}

							{/* Level 2 — Transaction detail */}
							{drillLevel === 'transactions' && drillCategory && (
								<>
									<button
										type="button"
										onClick={() => { setDrillLevel('category'); setDrillCategory(null); }}
										className="flex items-center gap-1 text-sm mb-3 hover:underline"
										style={{ color: DRILL_TEAL }}
									>
										<ArrowLeft className="h-4 w-4" />
										Back
									</button>
									<div className="text-sm text-muted-foreground mb-3">
										Individual transactions contributing to <strong>{drillCategory.label}</strong> — reconciles to <strong>{formatCurrency(drillCategory.amount, 'full')}</strong>
									</div>
									<table className="w-full" style={{ borderCollapse: 'collapse' }}>
										<thead>
											<tr style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' }}>
												<th style={{ padding: '10px 12px', fontSize: 14, fontWeight: 600, color: HDR_TXT, textAlign: 'left' }}>Description</th>
												<th style={{ padding: '10px 12px', fontSize: 14, fontWeight: 600, color: HDR_TXT, textAlign: 'left' }}>Source</th>
												<th style={{ padding: '10px 12px', fontSize: 14, fontWeight: 600, color: HDR_TXT, textAlign: 'right' }}>Amount</th>
											</tr>
										</thead>
										<tbody>
											{buildTransactionRows(drillCategory).map((txn, i) => (
												<tr key={txn.id} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: i % 2 === 0 ? '#ffffff' : '#f9fafb' }}>
													<td style={{ padding: '10px 12px', fontSize: 14, color: CONTENT_TXT }}>{txn.description}</td>
													<td style={{ padding: '10px 12px', fontSize: 14 }}>
														<span className="inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium text-muted-foreground bg-background">
															{txn.dataSourceName ?? 'Manual Entry'}
														</span>
													</td>
													<td style={{ padding: '10px 12px', fontSize: 14, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: drillCategory.type === 'inflow' ? 'var(--mc-success-icon)' : 'var(--mc-error-icon)' }}>
														{drillCategory.type === 'outflow' ? '−' : ''}{formatCurrency(Math.abs(txn.amount), 'full')}
													</td>
												</tr>
											))}
										</tbody>
										<tfoot>
											<tr style={{ borderTop: '2px solid #e5e7eb' }}>
												<td style={{ padding: '10px 12px', fontSize: 14, fontWeight: 700, color: CONTENT_TXT }}>Total</td>
												<td />
												<td style={{ padding: '10px 12px', fontSize: 14, fontWeight: 700, textAlign: 'right', color: drillCategory.type === 'inflow' ? 'var(--mc-success-icon)' : 'var(--mc-error-icon)', fontVariantNumeric: 'tabular-nums' }}>
													{drillCategory.type === 'outflow' ? '−' : ''}{formatCurrency(drillCategory.amount, 'full')}
												</td>
											</tr>
										</tfoot>
									</table>
								</>
							)}
						</div>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
};
